import { useState, useEffect, useRef, useCallback } from 'react';
import { useAtom } from 'jotai';
import { ChevronDown, FolderOpen, Plus, Github, MoreVertical, Pencil, Trash2, X, Loader2, AlertCircle, Database } from 'lucide-react';
import { currentProjectAtom, fileTreeAtom } from '@/stores/editorStore';
import { apiRequest } from '@/lib/api';
import type { Project } from '@shared/types';

interface ProjectSelectorProps {
  onNewProject: () => void;
  onGitHubImport: () => void;
}

export default function ProjectSelector({ onNewProject, onGitHubImport }: ProjectSelectorProps) {
  const [currentProject, setCurrentProject] = useAtom(currentProjectAtom);
  const [, setFileTree] = useAtom(fileTreeAtom);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  const [menuOpenForProject, setMenuOpenForProject] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    loadProjects();
  }, []);

  // Refresh projects list when currentProject changes to a project not in the list
  // This handles the case when a new project is created
  useEffect(() => {
    if (currentProject && !projects.find(p => p.id === currentProject.id)) {
      loadProjects();
    }
  }, [currentProject]);

  // Poll for project status updates when any project is installing
  useEffect(() => {
    const hasInstallingProjects = projects.some(p => p.dev_server_status === 'installing');

    if (hasInstallingProjects) {
      const interval = setInterval(() => {
        loadProjects();
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(interval);
    }
  }, [projects]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Only process if dropdown is open
      if (!isOpen) return;

      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setMenuOpenForProject(null);
      }
    };

    // Always attach listener, but only act when isOpen is true
    // This prevents issues when page loses/regains focus
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const response = await apiRequest<{ projects: Project[] }>('get', '/projects');
      setProjects(response.projects);

      // Only auto-select first project on initial load (when projects list is empty)
      // Don't auto-select on refreshes to avoid changing the user's selected project
      if (!currentProject && response.projects.length > 0 && projects.length === 0) {
        handleSelectProject(response.projects[0]);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectProject = useCallback(async (project: Project) => {
    setCurrentProject(project);
    setFileTree([]);
    setIsOpen(false);

    // File tree will auto-load via FileTree component's useEffect
  }, [setCurrentProject, setFileTree]);

  const handleDeleteProject = async (project: Project) => {
    // Optimistic update - remove from UI immediately
    setProjects(projects.filter(p => p.id !== project.id));

    // If we deleted the current project, clear it
    if (currentProject?.id === project.id) {
      setCurrentProject(null);
      setFileTree([]);
    }

    // Close modal immediately
    setDeletingProject(null);
    setIsOpen(false);

    // Delete in background (don't await)
    apiRequest('delete', `/projects/${project.id}`).catch(error => {
      console.error('Failed to delete project:', error);
      // Reload projects to restore if deletion failed
      loadProjects();
      alert('Failed to delete project. Please try again.');
    });
  };

  const handleEditProject = async (project: Project, newName: string, newDescription: string) => {
    try {
      await apiRequest('patch', `/projects/${project.id}`, {
        name: newName,
        description: newDescription,
      });

      // If we edited the current project, update it
      if (currentProject?.id === project.id) {
        setCurrentProject({
          ...currentProject,
          name: newName,
          description: newDescription,
        });
      }

      // Reload projects list
      await loadProjects();
      setEditingProject(null);
    } catch (error) {
      console.error('Failed to update project:', error);
      alert('Failed to update project. Please try again.');
    }
  };

  return (
    <>
      <div ref={dropdownRef} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md hover:bg-muted transition-colors"
        >
          <FolderOpen className="w-4 h-4" />
          <span className="font-medium max-w-[200px] truncate">
            {currentProject ? currentProject.name : 'Select Project'}
          </span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-80 bg-card border rounded-md shadow-lg z-50 max-h-80 overflow-auto">
            {isLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                Loading projects...
              </div>
            ) : projects.length === 0 ? (
              <div className="px-4 py-8 text-center space-y-3">
                <p className="text-sm text-muted-foreground mb-4">No projects yet - get started!</p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onNewProject();
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="font-medium">Create New Project</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onGitHubImport();
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
                  >
                    <Github className="w-4 h-4" />
                    <span className="font-medium">Import from GitHub</span>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="p-2 border-b space-y-1">
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onNewProject();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="font-medium">Create New Project</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onGitHubImport();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm bg-gray-900 text-white hover:bg-gray-800 rounded-md transition-colors"
                  >
                    <Github className="w-4 h-4" />
                    <span className="font-medium">Import from GitHub</span>
                  </button>
                </div>

                <div className="p-1">
                  {projects.map((project) => (
                    <div
                      key={project.id}
                      className={`relative group flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                        currentProject?.id === project.id
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-muted'
                      }`}
                    >
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelectProject(project);
                        }}
                        className="flex-1 flex items-center gap-2 text-left min-w-0"
                      >
                        <FolderOpen className="w-4 h-4 shrink-0" />
                        <div className="flex-1 overflow-hidden">
                          <p className="font-medium truncate">{project.name}</p>
                          {project.description && (
                            <p className="text-xs text-muted-foreground truncate">
                              {project.description}
                            </p>
                          )}
                        </div>
                        {project.template === 'github' && (
                          <Github className="w-3 h-3 text-muted-foreground shrink-0" />
                        )}
                        <div title={(project as any).has_database_connection ? "Database connected" : "No database connected"}>
                          <Database className={`w-3 h-3 shrink-0 ${(project as any).has_database_connection ? "text-green-500" : "text-yellow-500"}`} />
                        </div>
                        {project.dev_server_status === 'running' && (
                          <span className="w-2 h-2 bg-green-500 rounded-full shrink-0" title="Running" />
                        )}
                        {project.dev_server_status === 'installing' && (
                          <span title="Installing dependencies">
                            <Loader2 className="w-3 h-3 text-primary animate-spin shrink-0" />
                          </span>
                        )}
                        {project.dev_server_status === 'error' && (
                          <span title={project.error_message || 'Installation failed'}>
                            <AlertCircle className="w-3.5 h-3.5 text-destructive shrink-0" />
                          </span>
                        )}
                      </button>

                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpenForProject(menuOpenForProject === project.id ? null : project.id);
                          }}
                          className="p-1 hover:bg-muted rounded opacity-100 transition-opacity"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>

                        {menuOpenForProject === project.id && (
                          <div className="absolute right-0 top-full mt-1 w-40 bg-card border rounded-md shadow-lg z-[60]">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingProject(project);
                                setMenuOpenForProject(null);
                                setIsOpen(false);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted rounded-t-md transition-colors"
                            >
                              <Pencil className="w-4 h-4" />
                              <span>Edit</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeletingProject(project);
                                setMenuOpenForProject(null);
                                setIsOpen(false);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-b-md transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Edit Project Modal */}
      {editingProject && (
        <EditProjectModal
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSave={handleEditProject}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingProject && (
        <DeleteProjectModal
          project={deletingProject}
          onClose={() => setDeletingProject(null)}
          onConfirm={handleDeleteProject}
        />
      )}
    </>
  );
}

// Edit Project Modal Component
function EditProjectModal({
  project,
  onClose,
  onSave,
}: {
  project: Project;
  onClose: () => void;
  onSave: (project: Project, name: string, description: string) => void;
}) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Project name is required');
      return;
    }

    setIsSaving(true);
    await onSave(project, name.trim(), description.trim());
    setIsSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
      <div className="bg-card border rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Edit Project</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="project-name" className="block text-sm font-medium mb-1">
              Project Name
            </label>
            <input
              id="project-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background"
              placeholder="My Awesome Project"
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="project-description" className="block text-sm font-medium mb-1">
              Description (Optional)
            </label>
            <textarea
              id="project-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background resize-none"
              placeholder="A brief description of your project"
              rows={3}
            />
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 border rounded-md hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Delete Confirmation Modal Component
function DeleteProjectModal({
  project,
  onClose,
  onConfirm,
}: {
  project: Project;
  onClose: () => void;
  onConfirm: (project: Project) => void;
}) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    await onConfirm(project);
    setIsDeleting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
      <div className="bg-card border rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
            <Trash2 className="w-5 h-5 text-destructive" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold mb-1">Delete Project</h2>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete <strong>{project.name}</strong>? This action cannot be undone and all project files will be permanently deleted.
            </p>
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 border rounded-md hover:bg-muted transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors disabled:opacity-50"
          >
            {isDeleting ? 'Deleting...' : 'Delete Project'}
          </button>
        </div>
      </div>
    </div>
  );
}
